// src/pages/MeetingRoom/hooks/useMeetingController.js
import { useEffect, useRef, useState } from "react";
import useMeetingInstructions from "./useMeetingInstructions";
import useMeetingLifecycle from "./useMeetingLifeCycle";
import useMeetingSession from "./useMeetingSession";
import useMeetingFeedback from "./useMeetingFeedback";
import useAudioSetup from "./useAudioSetup";
import useMeetingTimer from "./useMeetingTimer";
import useSessionEndProposal from "./useSessionEndProposal";
import { useToast } from "../../../components/Toaster/use-toast";

export default function useMeetingController({ routeMeetingId, state, onBack, isTrainingMode = false, trainingPlanId }) {
  const { toast } = useToast();
  // 1️⃣ Lifecycle: meeting data, training session, conversation
  const lifecycle = useMeetingLifecycle({
    routeMeetingId,
    state,
    isTrainingMode,
    trainingPlanId,
  });

  // 2️⃣ Meeting instructions — must come BEFORE useMeetingSession
   const meetingInstructions = useMeetingInstructions({
       trainingSession: lifecycle.trainingSession,
     });

  // 3️⃣ Session management — now we can safely pass in applyInstructions
  const session = useMeetingSession({
    ...lifecycle,
    toast,
    applyInstructions: meetingInstructions.applyInstructions, // ✅ correct order
  });
  // 3️⃣ Timer Setup
  const timer = useMeetingTimer({ ...session, ...lifecycle });
  // 4️⃣ Handle Feedback
  const feedback = useMeetingFeedback({
    ...lifecycle,
    ...session,
    ...timer,
    ...meetingInstructions,
    toast,
  });

  // 5️⃣ Handle End Session Proposal
  const handleEndSessionRef = useRef(null);
  const setEndProposedRef = useRef(null);

  // Update ref so proposal can call this later
  handleEndSessionRef.current = feedback.handleEndSession;

  // Proposal
  const proposal = useSessionEndProposal({
    handleEndSession: () => handleEndSessionRef.current?.(),
  });

  // Store setter so feedback can clear it
  setEndProposedRef.current = proposal.setEndProposed;

  // Connect proposal → session
  useEffect(() => {
    if (proposal.onEndProposed && session.setEndProposalHandler) {
      session.setEndProposalHandler(proposal.onEndProposed);
    }
  }, [proposal.onEndProposed, session.setEndProposalHandler]);

  // 6️⃣ Audio + Instructions
  const audio = useAudioSetup({ toast });



  // 7️⃣ Sync devices
  useEffect(() => {
    if (session.sessionActive) audio.fetchDevices();
  }, [session.sessionActive]);




  // 🧩 Return unified controller
  return {
    ...lifecycle, // Meeting data, training session, etc.
    ...meetingInstructions,
    ...session,   // Session management (start, end, etc.)
    ...timer,     // Timer logic (session timer)
    ...feedback,  // Feedback logic
    ...proposal,  // End session proposal logic
    audio,        // Audio setup (input/output devices)
    toast,
    onBack,
  };
}
